import styled from 'styled-components';
import { theme } from '../../styles/theme';

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

export const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.textPrimary};
`;

export const Input = styled.input<{ $hasError?: boolean }>`
  padding: ${theme.spacing.md};
  border: 1px solid ${({ $hasError }) => $hasError ? theme.colors.error : 'rgba(255, 255, 255, 0.14)'};
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.md};
  background: rgba(255, 255, 255, 0.04);
  color: ${theme.colors.textPrimary};
  transition: all ${theme.transitions.normal};
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.06);

  &:focus {
    border-color: ${({ $hasError }) => $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 10px 35px ${({ $hasError }) => 
      $hasError ? 'rgba(248, 113, 113, 0.18)' : 'rgba(45, 138, 91, 0.2)'};
    transform: translateY(-1px);
  }

  &::placeholder {
    color: ${theme.colors.textTertiary};
  }

  &:disabled {
    background: ${theme.colors.gray100};
    color: ${theme.colors.textTertiary};
    cursor: not-allowed;
  }
`;

export const Button = styled.button<{ 
  $variant?: 'primary' | 'secondary' | 'outline';
  $size?: 'sm' | 'md' | 'lg';
  $fullWidth?: boolean;
  $loading?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.sm};
  padding: ${({ $size = 'md' }) => {
    switch ($size) {
      case 'sm': return `${theme.spacing.sm} ${theme.spacing.md}`;
      case 'lg': return `${theme.spacing.lg} ${theme.spacing.xl}`;
      default: return `${theme.spacing.md} ${theme.spacing.lg}`;
    }
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${({ $size = 'md' }) => {
    switch ($size) {
      case 'sm': return theme.fontSizes.sm;
      case 'lg': return theme.fontSizes.lg;
      default: return theme.fontSizes.md;
    }
  }};
  font-weight: ${theme.fontWeights.medium};
  line-height: 1;
  cursor: pointer;
  transition: all ${theme.transitions.fast};
  border: 2px solid transparent;
  text-decoration: none;
  width: ${({ $fullWidth }) => $fullWidth ? '100%' : 'auto'};
  position: relative;
  overflow: hidden;
  isolation: isolate;

  ${({ $variant = 'primary', $loading }) => {
    switch ($variant) {
      case 'secondary':
        return `
          background: rgba(255, 255, 255, 0.06);
          color: ${theme.colors.textPrimary};
          border-color: rgba(255, 255, 255, 0.12);
          
          &:hover:not(:disabled) {
            background: rgba(255, 255, 255, 0.12);
            border-color: rgba(255, 255, 255, 0.18);
          }
        `;
      case 'outline':
        return `
          background-color: transparent;
          color: ${theme.colors.primary};
          border-color: ${theme.colors.primary};
          
          &:hover:not(:disabled) {
            background-color: ${theme.colors.primary};
            color: ${theme.colors.white};
          }
        `;
      default:
        return `
          background: linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.primaryDark});
          color: ${theme.colors.white};
          box-shadow: 0 15px 45px rgba(45, 138, 91, 0.35);
          
          &:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 20px 50px rgba(45, 138, 91, 0.45);
          }
          
          &:active:not(:disabled) {
            transform: translateY(0);
          }
        `;
    }
  }}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }

  ${({ $loading }) => $loading && `
    pointer-events: none;
    
    &::after {
      content: '';
      position: absolute;
      width: 16px;
      height: 16px;
      border: 2px solid transparent;
      border-top-color: currentColor;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
    
    @keyframes spin {
      to {
        transform: rotate(360deg);
      }
    }
  `}
`;

export const ErrorMessage = styled.div`
  color: ${theme.colors.error};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const SuccessMessage = styled.div`
  color: ${theme.colors.success};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing.xs};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
`;

export const LinkButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  text-decoration: underline;
  padding: 0;
  
  &:hover {
    color: ${theme.colors.primaryDark};
  }
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  margin: ${theme.spacing.lg} 0;
  
  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: rgba(255, 255, 255, 0.12);
  }
  
  &::before {
    margin-right: ${theme.spacing.md};
  }
  
  &::after {
    margin-left: ${theme.spacing.md};
  }
`;

export const DividerText = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.textSecondary};
  background: rgba(7, 20, 15, 0.9);
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid rgba(255, 255, 255, 0.06);
`;
